import { NgIf } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { ButtonComponent } from '@components/button/button.component';
import { HeaderComponent } from '@components/header/header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, Location } from '@angular/common'
import { Router } from '@angular/router';
import { CosentinoSvgComponent } from '@components/cosentino-svg/cosentino-svg.component';
import { LinkComponent } from '@components/link/link.component';
import { ApiService } from 'app/services/api.service';
import { ColumnConfig, ColumnFormat, TableComponent } from '@components/table/table.component';
import { TransactionData } from 'app/interfaces/transaction-data.interface';
import { RedeemedReward } from 'app/interfaces/redeemed-reward.interface';
import { EmployeeSummary } from 'app/interfaces/employee-summary.interface';
import { MyPointsRoutingModule } from './mypoints-routing.module';
import { DynamicBannerComponent } from './dynamic-banner/dynamic-banner-component';
import { MenuService } from 'app/services/menu.service';
import { MenuItemType } from 'app/interfaces/api/MenuItemType';

@Component({
    selector: 'app-mypoints',
    templateUrl: './mypoints.component.html',
    styleUrl: './mypoints.component.scss',
    standalone: true,
    imports: [
        HeaderComponent,
        TranslateModule,
        NgIf,
        ButtonComponent,
        CosentinoSvgComponent,
        LinkComponent,
        CommonModule,
        TableComponent,
        MyPointsRoutingModule,
        DynamicBannerComponent,
    ],
})
export class MyPointsComponent {
    public currentSales = signal<number>(0)
    public currentStatus: string = ''
    public availableActios: number = 0
    public pendingValidationActios: number = 0
    public noNearExpirationActios: number = 0
    public soonToExpireActios: number = 0
    public actiosConditions: string = ''
    public accountOwnerEmail: string = ''
    public canExchangePoints: boolean = false
    public today = new Date()

    transactionColumns: ColumnConfig[] = [
        { key: 'ordinationDate', header: this.translateService.instant('myrewards.date'), format: 'date' as ColumnFormat },
        { key: 'fromId', header: this.translateService.instant('myrewards.identifier') },
        { key: 'status', header: this.translateService.instant('myrewards.status') },
        { key: 'contact', header: this.translateService.instant('myrewards.employee') },
        { key: 'validityDate', header: this.translateService.instant('myrewards.expiration'), format: 'date' as ColumnFormat },
        {
            key: 'strActios',
            header: this.translateService.instant('myrewards.points'),
            format: 'points' as ColumnFormat, // Es string tipo "+100"
        },
    ]
    employeeSummaryColumns: ColumnConfig[] = [
        { key: 'name', header: this.translateService.instant('myrewards.employee') },
        { key: 'accountName', header: this.translateService.instant('myrewards.account') },
        { key: 'enabledGlobalRewards', header: this.translateService.instant('myrewards.able_to_redeem'), format: 'boolean' as ColumnFormat },
        { key: 'wonActios', header: this.translateService.instant('myrewards.generated_points'), format: 'number' as ColumnFormat },
        { key: 'redeemedActios', header: this.translateService.instant('myrewards.redeemed_points'), format: 'number' as ColumnFormat },
    ]

    public transactionData: TransactionData[] = []
    public redeemedRewards: RedeemedReward[] = []
    public employeeSummaries: EmployeeSummary[] = []

    constructor(private location: Location, private router: Router, private api: ApiService, private translateService: TranslateService, private menuService: MenuService) {}

    ngOnInit() {
        const userInfo = localStorage.getItem('weUserInfo')
        if (userInfo) {
            const { ytdSales, loyaltyLevel, country, availableActios, linksByCountry, authorities, accountOwnerEmail } = JSON.parse(userInfo)
            this.currentSales.set(ytdSales)
            this.currentStatus = loyaltyLevel
            this.availableActios = availableActios
            this.accountOwnerEmail = accountOwnerEmail
            this.actiosConditions = linksByCountry.items.find((item: any) => item.code === 'actios-conditions').link
            this.canExchangePoints = authorities.some((authority: any) => authority === 'canExchangePoints')

            this.api.getLoyaltyPoints().subscribe({
                next: (points: any) => {
                    this.transactionData = points.transactions
                    this.mapPointsByStatus(this.transactionData)
                    this.redeemedRewards = points.redeemedRewards
                    this.employeeSummaries = points.employeeSummaries
                    this.pendingValidationActios = points.pendingValidation
                    this.soonToExpireActios = points.soonToExpire
                    this.noNearExpirationActios = points.noNearExpiration
                },
                error: (error: any) => console.log(error),
            })
            // this.api.getPointsTransactions().subscribe({next:()=> {},error: (error:any) => console.log(error)})
            // this.api.getPointsEmployeeSummary().subscribe({next:()=> {},error: (error:any) => console.log(error)})
            // this.api.getPointsRedeemed().subscribe({next:()=> {},error: (error:any) => console.log(error)})
        }
    }

    mapPointsByStatus(transactionData: TransactionData[]) {
        transactionData.map((transaction) => {
            switch (transaction.status) {
                case 'Won':
                    transaction.strActios = `+${transaction.actios}`
                    break
                case 'Pending':
                    transaction.strActios = `${transaction.actios}`
                    break
                default:
                    transaction.strActios = `-${transaction.actios}`
                    break
            }
            return transaction;
        })
    }

    goBack() {
        this.location.back()
    }
    goToMyPoints() {
        this.router.navigate(['/retail-rewards'])
    }
    goToTransactionSummary() {
        this.router.navigate(['/my-points/transaction-summary'])
    }
    goToEmployeeSummary() {
        this.router.navigate(['/my-points/employee-summary'])
    }
    goToRedeemedRewards() {
        this.router.navigate(['/my-points/redeemed-rewards'])
    }
    redeemPoints() {
        if (!this.canExchangePoints) return
        this.api.getRewardsProviderUrl().subscribe({
            next: (next) => {
                this.menuService.goTo({ type: MenuItemType.EXTERNAL_LINK, code: 'xoxoday', link: next.redirectUrl, requiresContract: 'retail-rewards' })
                // window.open(next.redirectUrl, '_blank')
            },
        })
    }
    knowYourBenefits() {
        this.router.navigate(['/retail-rewards'])
    }
    sendEmail() {
        const to = this.accountOwnerEmail
        const subject = 'Rewards Program'

        // Codificamos para que los espacios y acentos funcionen bien
        const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}}`

        window.location.href = mailto
    }

    getTotalRedeemedPoints(): number {
        return this.redeemedRewards.reduce((sum, item) => sum + (item.pointCost || 0), 0);
    }
}
